import {classList, listen, dispatch, dataset, cssVars, lazyCustomElements} from 'nuffjs';

const KEY_ENTER = 13;
const KEY_SPACE = 32;

/**
 * Configuration values
 *
 * @property {string} idAttr=id the attribute use to find the targets
 * @property {string} disabledClass=disabled class to signal a disabled action in case is
 * implemented with a link instead of a button
 * @property {string} busyClass=busy class used to flag the component is busy performing the action
 * @property {string} listenEvent=click the event to listen for
 * @property {string} beforeExecuteEvent=action:beforeExecute cancelable event raised before
 * performing the action
 * @property {string} executeEvent=action:execute event raised after performing the action
 * @property {string} disabledByCssProp=actionDisabled CSS variable that can be used to disable the
 * component in certain media queries or contexts
 * @memberof Action
 */
const conf = {
	idAttr: 'id',
	disabledClass: 'disabled',
	busyClass: 'busy',
	listenEvent: 'click',
	beforeExecuteEvent: 'action:beforeExecute',
	executeEvent: 'action:execute',
	disabledByCssProp: 'actionDisabled',
};

/**
 * create the Action class with a dynamic super class
 *
 * @mixin
 * @param {typeof HTMLElement} Base base class to be extended
 * @returns {typeof Action} the Action class extending the Base class
 */
const actionMixin = (Base) =>
	/**
	 * @classdesc Basic cliccable component used to call methods on other components. Usually
	 * augments a button or a link.
	 *
	 * @class Action
	 * @extends HTMLElement
	 * @version 1.0.2
	 * @path
	 *
	 * @emits action:beforeExecute
	 * @emits action:execute
	 * @listens click
	 *
	 */
	class extends Base {
		static conf = conf;

		/**
		 * Default options, overridable by data-attributes
		 *
		 * @property {?(string|string[])} for=null the parameter pointing to the target id or an
		 * array of ids
		 * @property {?string} forSelector=null alternative custom selector to find the targets
		 * @property {?string} command=null the name of the method to be called on the targets
		 * @property {array} params=[] list of parameter to be passed to the command when called
		 * @property {boolean} chainCalls=false true when you allow a call to be scheduled when the
		 * component is still busy with the previous one. When false, the call when busy will be
		 * ignored instead
		 * @property {boolean} waitForTargets=false true when you want the component to wait the end
		 * of the command execution on the target(s)
		 * @memberof Action
		 *
		 * @static
		 */
		static defaultOpts = {
			for: null,
			forSelector: null,
			command: null,
			params: [],
			chainCalls: false,
			waitForTargets: false,
		};

		busy = false;
		promise = Promise.resolve();

		/**
		 * Initialization
		 *
		 * @memberof Action
		 * @instance
		 */
		firstConnectedCallback() {
			this.opts = dataset(this).get(this.constructor.defaultOpts);
			this.isButton = this instanceof HTMLButtonElement;

			listen(this, conf.listenEvent, this.onAction.bind(this));
			if (
				conf.listenEvent === 'click' &&
				!this.isButton &&
				!(this instanceof HTMLAnchorElement) &&
				this.getAttribute('role') === 'button'
			) {
				listen(this, 'keypress', (event) => {
					const code = event.charCode || event.keyCode;
					if (code === KEY_SPACE || code === KEY_ENTER) {
						this.onAction(event);
					}
				});
			}
		}

		/**
		 * Handles the user interaction, usually a click
		 *
		 * @param {Event} event the event handled
		 * @memberof Action
		 * @instance
		 */
		onAction(event) {
			if (this.isEnabled() && !event.defaultPrevented) {
				if (this instanceof HTMLAnchorElement) {
					event.preventDefault();
				}
				this.execute();
			}
		}

		/**
		 * Perform the call to the target(s) components' method
		 *
		 * @param {?array} [params=null] if passed, overrides the params passed in the data
		 * attributes
		 * @returns {promise<boolean>} resolved when the execution is performed, with true if it the
		 * command was performed
		 * @emits action:beforeExecute
		 * @emits action:execute
		 * @memberof Action
		 * @instance
		 *
		 */
		async execute(params = null) {
			const opts = this.opts;
			const cl = classList(this);
			if (!this.isEnabled() || (this.busy && !opts.chainCalls)) {
				return false;
			}
			const targets = await this.getTargets();
			if (!targets.length) {
				return false;
			}
			const command = opts.command;
			if (!command) {
				throw Error('Command not defined');
			}
			if (params === null) {
				params = opts.params;
			}
			const beforeEvent = dispatch(this, conf.beforeExecuteEvent, {
				bubbles: true,
				cancelable: true,
				detail: {targets: targets, command: command, params: params},
			});
			if (beforeEvent.defaultPrevented) {
				return false;
			}
			this.busy = true;
			cl.add(conf.busyClass);
			const promise = (opts.chainCalls ? this.promise : Promise.resolve())
				.then(() => this.executing(targets, command, params))
				.then(() => {
					this.busy = false;
					cl.remove(conf.busyClass);
					dispatch(this, conf.executeEvent, {
						bubbles: true,
						cancelable: false,
						detail: {targets: targets, command: command, params: params},
					});
					return true;
				});
			this.promise = promise;
			return this.promise;
		}

		/**
		 * The component can be disabled by a disabled attribute (when is a button), by a css
		 * disabled class or by a css variable
		 *
		 * @returns {boolean}
		 * @memberof Action
		 * @instance
		 */
		isEnabled() {
			return !(
				(this.isButton && this.disabled) ||
				classList(this).contains(conf.disabledClass) ||
				cssVars(this).get(conf.disabledByCssProp, false)
			);
		}

		/**
		 * Enable when is disabled
		 *
		 * @memberof Action
		 * @instance
		 */
		enable() {
			this.toggleEnabled(true);
		}

		/**
		 * Disable when is enabled
		 *
		 * @memberof Action
		 * @instance
		 */
		disable() {
			this.toggleEnabled(false);
		}

		/**
		 * Used internally to handle the enable / disable
		 *
		 * @param {?boolean} [value=null] when not passed, it will invert the current status
		 * @memberof Action
		 * @instance
		 * @protected
		 * @ignore
		 */
		toggleEnabled(value = null) {
			const currentValue = this.isEnabled();
			if (value === null) {
				value = !currentValue;
			}
			if (value !== currentValue) {
				if (this.isButton) {
					this.disabled = !value;
				} else {
					classList(this).toggle(conf.disabledClass, !value);
				}
			}
		}

		/**
		 * Internal method to execute the command
		 *
		 * @param {Element[]} targets the list of targets, typically only one
		 * @param {string} command the method name to be called
		 * @param {array} params the parameters for the method call
		 * @returns {Promise} resolves immediately when the option waitForTargets is false,
		 * otherwise when all the target(s) commands finish their execution
		 * @memberof Action
		 * @instance
		 * @protected
		 * @ignore
		 */
		executing(targets, command, params) {
			const p = Promise.all(
				targets
					.filter((target) => target && command in target && target[command] instanceof Function)
					.map((target) => target[command](...params))
			);
			return this.opts.waitForTargets ? p : Promise.resolve();
		}

		/**
		 * Internal method to select the targets. First check the forSelector option, if not present
		 * the for option, if not present and the component is a link with href starting with #
		 * tries to use that as an id to select a target
		 *
		 * @returns {Promise<Element[]>} resolve with the array of selected targets
		 * @memberof Action
		 * @instance
		 * @protected
		 * @ignore
		 */
		async getTargets() {
			const opts = this.opts;
			let selector = opts.forSelector;
			if (!selector) {
				let ids = opts.for;
				if (ids) {
					ids = Array.isArray(ids) ? ids : [ids];
					selector = ids.map((id) => dataset.selector(conf.idAttr, id)).join(',');
				} else if (this.hasAttribute('href')) {
					const href = this.getAttribute('href');
					if (href.indexOf('#') === 0) {
						selector = href;
					}
				}
			}
			return selector
				? [...(await lazyCustomElements.querySelectorAll(this.getRootNode(), selector))]
				: [];
		}
	};

export {actionMixin, conf as actionConf};
